import { useCallback, useEffect, useState } from "react";
import { Search, X, Tags, ChevronLeft, ChevronRight, Loader2, Briefcase, ListChecks } from "lucide-react";
import { useTheirStackJobs } from "./modal/TheirStackJobsContext";
import JobCard from "./ui/JobCard";
import AssignmentCard from "./ui/AssignmentCard";
import AdminJobDetailModal from "./ui/AdminJobDetailModal";
import AssignmentDetailModal from "./ui/AssignmentDetailModal";
import TechTagsModal from "./ui/TechTagsModal";
import { STATUS_CHIP_SOLID, TERMINAL } from "./ui/shared";
import { fetchAssignments, STATUS_OPTIONS, type AssignmentStatus } from "./api/TheirStackJobsApi";
import AppSelect from "shared/AppSelect";
import ViewToggle from "shared/ViewToggle";
import StatusKanbanBoard, { type KanbanColumn } from "shared/StatusKanbanBoard";

const STATUS_ORDER = [
  "new", "reviewing", "applied", "interviewing",
  "offer_received", "hired", "rejected", "not_relevant", "archived",
];

const ASSIGNMENTS_VIEW_KEY = "hrms_theirstack_jobs_view";
const BOARD_COLUMNS: KanbanColumn[] = STATUS_OPTIONS.map((o) => ({
  key: o.value,
  label: o.label,
  isTerminal: TERMINAL.has(o.value),
}));

export default function TheirStackJobsBody() {
  const {
    isAdmin,
    jobs,
    jobsLoading,
    jobFilters,
    setJobFilters,
    loadJobs,
    assignments,
    assignmentsLoading,
    assignmentFilters,
    setAssignmentFilters,
    loadAssignments,
    statusCounts,
    loadStatusCounts,
    techManagers,
    techManagersLoaded,
    loadTechManagers,
    selectedJob,
    openJob,
    closeJob,
    selectedAssignment,
    assignmentDetailLoading,
    openAssignment,
    closeAssignment,
    editingTechManager,
    openTechTagsModal,
    closeTechTagsModal,
    handleReassignAssignment,
    handleStatusUpdate,
  } = useTheirStackJobs();

  // Admin: default view is "Assignments" — the status-tracked pipeline view
  // (matching LinkedIn Email Alerts), where the Employee dropdown filters it
  // (undefined = every TM's assignments, one row per job+TM pair; a TM's id =
  // just theirs — a job can be auto-assigned to several TMs at once, so a raw
  // job doesn't map to a single status there). "Browse Jobs" is the raw job
  // list, where the Employee dropdown filter and per-job assignment
  // management live instead. Employees always see their own assignments only.
  const [browsingJobs, setBrowsingJobs] = useState(false);
  const isAssignmentsMode = isAdmin ? !browsingJobs : true;

  const [assignmentsView, setAssignmentsView] = useState<"list" | "board">(() =>
    localStorage.getItem(ASSIGNMENTS_VIEW_KEY) === "list" ? "list" : "board"
  );
  const switchAssignmentsView = (v: "list" | "board") => {
    setAssignmentsView(v);
    localStorage.setItem(ASSIGNMENTS_VIEW_KEY, v);
  };
  // Board shows every status as its own column, each paginated independently
  // (hide_archived only makes sense for the flat list) — keeps every other
  // filter in sync with the list.
  const fetchBoardColumn = useCallback(async (statusKey: string, page: number, pageSize: number) => {
    const result = await fetchAssignments(
      {
        page,
        page_size: pageSize,
        status_filter: statusKey,
        search: assignmentFilters.search || undefined,
        seniority: assignmentFilters.seniority || undefined,
        employment_type: assignmentFilters.employment_type || undefined,
        hide_archived: false,
        employee_id: assignmentFilters.employee_id,
        company_can_apply_only: assignmentFilters.company_can_apply_only || undefined,
      },
      isAdmin,
    );
    return { items: result.items, total: result.total_count };
  }, [assignmentFilters, isAdmin]);

  // Load tech managers for admin's Employee dropdown / Tech Tags menu
  useEffect(() => {
    if (isAdmin) loadTechManagers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Load the active view's data — fires on mount and whenever Assignments/Browse
  // Jobs mode changes
  useEffect(() => {
    if (isAssignmentsMode) {
      loadAssignments();
      loadStatusCounts();
    } else {
      loadJobs();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isAssignmentsMode]);

  // Reload jobs on filter changes (Browse Jobs mode only)
  useEffect(() => {
    if (isAdmin && !isAssignmentsMode) loadJobs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobFilters]);

  // Reload assignments on filter changes — covers Employee selection changes too
  useEffect(() => {
    if (!isAdmin || isAssignmentsMode) loadAssignments();
    if (isAdmin && isAssignmentsMode) loadStatusCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentFilters]);

  // Local search debounce
  const [localJobSearch, setLocalJobSearch] = useState(jobFilters.search);
  const [localAssignSearch, setLocalAssignSearch] = useState(assignmentFilters.search);

  useEffect(() => {
    const t = setTimeout(() => setJobFilters({ search: localJobSearch, page: 1 }), 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localJobSearch]);

  useEffect(() => {
    const t = setTimeout(() => setAssignmentFilters({ search: localAssignSearch, page: 1 }), 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localAssignSearch]);

  // Tech Tags management dropdown (independent of the Employee filter select)
  const [techTagsMenuOpen, setTechTagsMenuOpen] = useState(false);

  const totalStatusCount = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const trackedCount = isAssignmentsMode ? (assignments?.total_count ?? totalStatusCount) : (jobs?.total_count ?? 0);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">Other Jobs</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            {isAdmin
              ? "A separate, automated job-sourcing pipeline — not the LinkedIn Email Alerts jobs. Manage listings and TM assignments here."
              : "A separate, automated job-sourcing pipeline — not your LinkedIn Email Alerts jobs. Your assignments from this pipeline."}
          </p>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            {trackedCount} job{trackedCount !== 1 ? "s" : ""} tracked
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Employee filter — narrows the Assignments view to one TM's pipeline.
              Kept BEFORE the Browse Jobs/Assignments toggle: this row is
              right-anchored (parent uses justify-between), so a variable-width
              item placed before the toggle shifts the whole group's left edge
              by the same amount it displaces the toggle — net zero movement.
              Putting it after the toggle would make the toggle buttons visibly
              jump left/right every time this dropdown shows/hides. */}
          {isAdmin && isAssignmentsMode && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground font-inter whitespace-nowrap">Employee:</label>
              <AppSelect
                value={assignmentFilters.employee_id?.toString() ?? ""}
                onChange={(v) => setAssignmentFilters({ employee_id: v ? parseInt(v, 10) : undefined, page: 1 })}
                className="w-48"
              >
                <option value="">All TMs</option>
                {techManagers.map((tm) => (
                  <option key={tm.id} value={tm.id.toString()}>{tm.name}</option>
                ))}
              </AppSelect>
            </div>
          )}

          {/* Browse Jobs vs Assignments — Browse Jobs (the raw job list) is the
              default/first view; Assignments is the status-tracked pipeline view
              (matching Email Alerts). */}
          {isAdmin && (
            <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-0.5">
              <button
                onClick={() => setBrowsingJobs(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-inter font-medium transition-colors ${
                  !isAssignmentsMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Briefcase className="w-3.5 h-3.5" />
                Browse Jobs
              </button>
              <button
                onClick={() => setBrowsingJobs(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-inter font-medium transition-colors ${
                  isAssignmentsMode ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ListChecks className="w-3.5 h-3.5" />
                Assignments
              </button>
            </div>
          )}

          {isAssignmentsMode && (
            <ViewToggle view={assignmentsView} onChange={switchAssignmentsView} />
          )}

          {/* Admin: Tech Tags management */}
          {isAdmin && (
            <div className="relative">
              <button
                onClick={() => setTechTagsMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-4 py-2 border border-border hover:border-primary/50 text-foreground hover:text-foreground rounded-xl text-sm font-inter transition-colors"
              >
                <Tags className="w-4 h-4" />
                Tech Tags
              </button>
              {techTagsMenuOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setTechTagsMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 z-40 w-72 bg-surface-muted border border-border rounded-xl shadow-xl overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-xs font-semibold text-foreground font-inter">Manage TM Tech Tags</p>
                      <p className="text-[11px] text-muted-foreground font-inter mt-0.5">
                        Tags are used to auto-assign jobs by stack_source
                      </p>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {!techManagersLoaded ? (
                        <div className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground font-inter">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                        </div>
                      ) : techManagers.length === 0 ? (
                        <p className="px-4 py-3 text-xs text-muted-foreground font-inter italic">
                          No Technical Managers found.
                        </p>
                      ) : (
                        techManagers.map((tm) => (
                          <button
                            key={tm.employee_code}
                            onClick={() => {
                              setTechTagsMenuOpen(false);
                              openTechTagsModal(tm);
                            }}
                            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-muted/50 transition-colors text-left border-b border-border/50 last:border-0"
                          >
                            <div className="w-7 h-7 rounded-full bg-primary/30 flex items-center justify-center shrink-0 text-xs font-bold text-primary font-inter">
                              {tm.name.charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-foreground font-inter">{tm.name}</p>
                              <p className="text-[11px] text-muted-foreground font-inter truncate">
                                {tm.tech_tags.length > 0 ? tm.tech_tags.join(", ") : "No tags"}
                              </p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Browse Jobs (admin) ──────────────────────────────────────────────── */}
      {!isAssignmentsMode && isAdmin && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={localJobSearch}
                onChange={(e) => setLocalJobSearch(e.target.value)}
                placeholder="Search jobs…"
                className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
              />
              {localJobSearch && (
                <button onClick={() => setLocalJobSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
              <input
                type="checkbox"
                checked={jobFilters.active_only}
                onChange={(e) => setJobFilters({ active_only: e.target.checked, page: 1 })}
                className="rounded border-border text-primary focus:ring-ring"
              />
              Active only
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
              <input
                type="checkbox"
                checked={jobFilters.unassigned_only}
                onChange={(e) => setJobFilters({ unassigned_only: e.target.checked, page: 1 })}
                className="rounded border-border text-primary focus:ring-ring"
              />
              Unassigned only
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
              <input
                type="checkbox"
                checked={jobFilters.company_can_apply_only}
                onChange={(e) => setJobFilters({ company_can_apply_only: e.target.checked, page: 1 })}
                className="rounded border-border text-amber-500 focus:ring-amber-500"
              />
              Direct to Company only
            </label>
          </div>

          {/* Job list */}
          {jobsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !jobs || jobs.items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground font-inter text-sm">No jobs found.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {jobs.items.map((job) => (
                  <JobCard key={job.id} job={job} onClick={() => openJob(job)} />
                ))}
              </div>
              <Pagination
                page={jobs.page}
                totalPages={jobs.total_pages}
                totalCount={jobs.total_count}
                onChange={(p) => setJobFilters({ page: p })}
              />
            </>
          )}
        </>
      )}

      {/* ── Assignments (all TMs, one TM, or the employee's own view) ──────────── */}
      {isAssignmentsMode && (
        <>
          {/* Status count strip — a status filter doesn't make sense in board
              view, since the board already shows every status as its own column */}
          {assignmentsView === "list" && (
            <div className="flex flex-wrap gap-1.5">
              <button
                onClick={() => setAssignmentFilters({ status_filter: "", hide_archived: true, page: 1 })}
                className={`text-xs px-2.5 py-1 rounded-lg border font-inter transition-colors ${
                  !assignmentFilters.status_filter
                    ? "bg-muted text-foreground border-border"
                    : "text-muted-foreground border-border hover:border-border hover:text-foreground"
                }`}
              >
                All ({totalStatusCount})
              </button>
              {STATUS_ORDER.map((s) => {
                const chip = STATUS_CHIP_SOLID[s];
                const isSelected = assignmentFilters.status_filter === s;
                const count = statusCounts[s] ?? 0;
                return (
                  <button
                    key={s}
                    onClick={() => setAssignmentFilters({ status_filter: s, hide_archived: false, page: 1 })}
                    className={`text-[11px] px-2.5 py-1 rounded-lg border font-inter transition-colors ${
                      isSelected
                        ? chip.className
                        : "text-muted-foreground border-border hover:border-border hover:text-foreground"
                    }`}
                  >
                    {chip.label}{count > 0 ? ` (${count})` : ""}
                  </button>
                );
              })}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={localAssignSearch}
                onChange={(e) => setLocalAssignSearch(e.target.value)}
                placeholder="Search assignments…"
                className="w-full bg-surface border border-border rounded-xl pl-9 pr-3 py-2 text-sm text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
              />
              {localAssignSearch && (
                <button onClick={() => setLocalAssignSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {assignmentsView === "list" && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={assignmentFilters.hide_archived}
                  onChange={(e) => setAssignmentFilters({ hide_archived: e.target.checked, status_filter: "", page: 1 })}
                  className="rounded border-border text-primary focus:ring-ring"
                />
                Hide archived
              </label>
            )}
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground font-inter cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={assignmentFilters.company_can_apply_only}
                  onChange={(e) => setAssignmentFilters({ company_can_apply_only: e.target.checked, page: 1 })}
                  className="rounded border-border text-amber-500 focus:ring-amber-500"
                />
                Direct to Company only
              </label>
            )}
          </div>

          {/* Board view */}
          {assignmentsView === "board" && (
            <StatusKanbanBoard
              columns={BOARD_COLUMNS}
              getId={(a) => a.assignment_id}
              getStatus={(a) => a.status}
              withStatus={(a, status) => ({ ...a, status: status as AssignmentStatus })}
              fetchColumnItems={fetchBoardColumn}
              onStatusChange={(a, status) => handleStatusUpdate(a.assignment_id, status as AssignmentStatus, null)}
              renderCard={(a) => {
                const tmName = isAdmin
                  ? techManagers.find((tm) => tm.id === a.employee_id)?.name
                  : undefined;
                return (
                  <AssignmentCard
                    assignment={a}
                    showEmployee={isAdmin}
                    employeeName={tmName}
                    techManagers={isAdmin ? techManagers : undefined}
                    onReassign={isAdmin ? handleReassignAssignment : undefined}
                    onStatusChange={handleStatusUpdate}
                    onClick={() => openAssignment(a)}
                    compact
                  />
                );
              }}
            />
          )}

          {/* Assignment list */}
          {assignmentsView === "list" && (assignmentsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : !assignments || assignments.items.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground font-inter text-sm">No assignments found.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignments.items.map((a) => {
                  const tmName = isAdmin
                    ? techManagers.find((tm) => tm.id === a.employee_id)?.name
                    : undefined;
                  return (
                    <AssignmentCard
                      key={a.assignment_id}
                      assignment={a}
                      showEmployee={isAdmin}
                      employeeName={tmName}
                      techManagers={isAdmin ? techManagers : undefined}
                      onReassign={isAdmin ? handleReassignAssignment : undefined}
                      onStatusChange={handleStatusUpdate}
                      onClick={() => openAssignment(a)}
                    />
                  );
                })}
              </div>
              <Pagination
                page={assignments.page}
                totalPages={assignments.total_pages}
                totalCount={assignments.total_count}
                onChange={(p) => setAssignmentFilters({ page: p })}
              />
            </>
          ))}
        </>
      )}

      {/* Modals */}
      {selectedJob && (
        <AdminJobDetailModal job={selectedJob} onClose={closeJob} />
      )}
      {selectedAssignment && (
        <AssignmentDetailModal
          assignment={selectedAssignment}
          detailLoading={assignmentDetailLoading}
          isAdmin={isAdmin}
          onClose={closeAssignment}
        />
      )}
      {editingTechManager && (
        <TechTagsModal techManager={editingTechManager} onClose={closeTechTagsModal} />
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  totalCount,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalCount: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-2">
      <p className="text-xs text-muted-foreground font-inter">{totalCount} total</p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-xs text-muted-foreground font-inter">
          {page} / {totalPages}
        </span>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
